import { useEffect, useMemo, useRef, useState } from 'react';

const ITEM_HEIGHT = 40;

export function buildSlotMap(slotData, type, offsetMin = 120) {
  const slotMap = {};
  const hourSet = new Set();

  for (const { fromTime, quota, quotaConsumed } of slotData) {
    if (quota - quotaConsumed <= 0) continue; // no quota left

    let [hour, minute] = fromTime.split(':').map(Number);

    // adding offset for “to” picker
    if (type === 'to') {
      const total = hour * 60 + minute + offsetMin;
      hour = Math.floor(total / 60) % 24;
      minute = total % 60;
    }

    if (!slotMap[hour]) slotMap[hour] = [];
    slotMap[hour].push(minute.toString().padStart(2, '0'));
    hourSet.add(Number(hour));
  }

  const hours = Array.from(hourSet).sort((a, b) => a - b); //sorting hours
  return { slotMap, hours };
}

/* ───────── Column ───────── */

const Column = ({ values, type, activeIndex, onScroll }) => {
  const ref = useRef(null);

  // keep selected row centered
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = activeIndex * ITEM_HEIGHT;
  }, [activeIndex]);

  const handleScroll = e => {
    const idx = Math.round(e.currentTarget.scrollTop / ITEM_HEIGHT);
    onScroll(type, idx);
  };

  return (
    <div ref={ref} className="slot-column" onScroll={handleScroll}>
      <div style={{ height: ITEM_HEIGHT }} />
      {values.map((v, i) => (
        <div key={i} className={`slot-item ${activeIndex === i ? 'active' : ''}`}>
          {type === 'hour' ? String(Number(v) % 12 || 12).padStart(2, '0') : type === 'minute' ? String(v).padStart(2, '0') : v}
        </div>
      ))}
      <div style={{ height: ITEM_HEIGHT }} />
    </div>
  );
};

/* ───────── SlotRangePicker ───────── */

const SlotRangePicker = ({ slotData, onChange, type, fromTime, toTime, initialTime, active, setActive, intervalData }) => {
  const [ampm, setAmPm] = useState(0);

  //   const rangeInterval = projectConfig?.data?.slotInterval;
  const rangeInterval = intervalData ?? 0;

  const { slotMap, hours } = useMemo(() => buildSlotMap(slotData, type, rangeInterval), [slotData, type, rangeInterval]);

  const minuteValues = useMemo(() => {
    const hourVal = hours[active.hour] ?? hours[0];
    return slotMap[hourVal] ?? [];
  }, [hours, active.hour, slotMap]);

  const meridiems = useMemo(() => {
    const selectedHour = hours[active.hour] ?? hours[0];
    const selectedMinute = slotMap[selectedHour]?.[active.minute];

    const counterpartHour = selectedHour < 12 ? selectedHour + 12 : selectedHour - 12;

    return selectedHour < 12 ? ['AM'] : ['PM'];
  }, [hours, active.hour, active.minute, slotMap]);

  // Ensures that active.minute doesn't go out of bounds
  useEffect(() => {
    if (minuteValues.length === 0) return;

    if (active.minute >= minuteValues.length) {
      setActive(prev => ({ ...prev, minute: 0 }));
    }
  }, [minuteValues, active.minute]);

  useEffect(() => {
    if (meridiems.length === 1) {
      if (ampm !== 0) setAmPm(0);
      return;
    }

    const ampmIdx = hours[active.hour] >= 12 ? 1 : 0;
    if (ampmIdx !== ampm) {
      setAmPm(ampmIdx);
    }
  }, [active.hour]);

  useEffect(() => {
    if (!onChange) return;
    const h = String(hours[active.hour]).padStart(2, '0');
    const m = minuteValues[active.minute];
    onChange(`${h}:${m}`);
  }, [active, hours, minuteValues, meridiems, onChange]);

  useEffect(() => {
    if (!initialTime) return;

    const [hour, minute] = initialTime.split(':').map(Number);
    const targetHour = hours.find(h => h === hour);
    const availableMinutes = slotMap[targetHour ?? hours[0]];
    const targetMinute = availableMinutes?.find(m => Number(m) === minute);

    setActive({
      hour: hours.indexOf(targetHour ?? hours[0]),
      minute: availableMinutes?.indexOf(targetMinute ?? availableMinutes[0]) ?? 0,
    });
    setAmPm(meridiems.indexOf(hour >= 12 ? 'PM' : 'AM'));
  }, []);

  const handleScroll = (type, idx) => {
    if (type === 'ampm') {
      setAmPm(idx);
    } else {
      setActive(prev => ({ ...prev, [type]: idx }));
    }
  };

  return (
    <div className="slot-picker">
      <Column values={hours} type="hour" activeIndex={active.hour} onScroll={handleScroll} />

      <Column values={minuteValues} type="minute" activeIndex={active.minute} onScroll={handleScroll} />

      <Column values={meridiems} type="ampm" activeIndex={ampm} onScroll={handleScroll} />
    </div>
  );
};

export default SlotRangePicker;
